/**
 * Catálogos de pruebas del atletismo:
 *  - `CANONICAL_PRUEBA_ORDER` : orden de presentación coherente con el
 *    programa de los campeonatos (velocidad → fondo → vallas →
 *    obstáculos → saltos → lanzamientos → combinadas → marcha → relevos).
 *  - `EVENTS_ADULT` / `EVENTS_SUB18` / ... : conjuntos de pruebas
 *    oficiales por categoría. Útil para filtrar ruido cuando el
 *    dataset etiqueta marcas con categorías inválidas.
 *
 * Las claves son normalizadas con `pruebaKey` (sin espacios, lowercase,
 * sin acentos).
 */

export const CANONICAL_PRUEBA_ORDER: string[] = [
  // Velocidad
  '50m', '60m', '80m', '100m', '150m', '200m', '250m', '300m', '400m',
  // Medio fondo / fondo
  '500m', '600m', '800m', '1000m', '1500m', '2000m', '3000m', '5000m', '10000m',
  // Vallas
  '50mv', '60mv', '80mv', '100mv', '110mv', '220mv', '300mv', '400mv',
  // Obstáculos
  '1500mobst', '2000mobst', '3000mobst',
  // Saltos
  'altura', 'pertiga', 'longitud', 'triple',
  // Lanzamientos
  'peso', 'disco', 'martillo', 'jabalina',
  // Combinadas
  'triatlon', 'tetratlon', 'pentatlon', 'hexatlon', 'heptatlon', 'octatlon', 'decatlon',
  // Marcha
  '1000marcha', '2000marcha', '3000marcha', '5000marcha', '10000marcha', '20000marcha',
  // Relevos
  '4x50m', '4x60m', '4x80m', '4x100m', '4x200m', '4x300m', '4x400m',
];

export const CANONICAL_INDEX: Map<string, number> = new Map(
  CANONICAL_PRUEBA_ORDER.map((k, i) => [k, i]),
);

export const EVENTS_ADULT: ReadonlySet<string> = new Set([
  '60m', '100m', '200m', '400m', '800m', '1500m', '3000m', '5000m', '10000m',
  '60mv', '100mv', '110mv', '400mv',
  '3000mobst',
  'altura', 'pertiga', 'longitud', 'triple',
  'peso', 'disco', 'martillo', 'jabalina',
  // Combinadas: heptatlón (AL mujer / PC hombre), decatlón (AL hombre),
  // pentatlón (PC mujer). El dataset RFEA no separa AL/PC en `estilo` para
  // combinadas, así que basta con incluir las cuatro claves.
  'pentatlon', 'heptatlon', 'decatlon',
  '3000marcha', '5000marcha', '10000marcha', '20000marcha',
  '4x100m', '4x400m',
]);

export const EVENTS_SUB18: ReadonlySet<string> = new Set([
  '60m', '100m', '200m', '400m', '800m', '1500m', '3000m', '5000m',
  '60mv', '100mv', '110mv', '400mv',
  '2000mobst', '3000mobst',
  'altura', 'pertiga', 'longitud', 'triple',
  'peso', 'disco', 'martillo', 'jabalina',
  'pentatlon', 'heptatlon', 'decatlon',
  '3000marcha', '5000marcha', '10000marcha',
  '4x100m', '4x400m',
]);

export const EVENTS_SUB16: ReadonlySet<string> = new Set([
  '60m', '80m', '100m', '150m', '300m', '600m', '1000m', '1500m', '2000m', '3000m',
  '60mv', '80mv', '100mv', '300mv',
  '1500mobst',
  'altura', 'pertiga', 'longitud', 'triple',
  'peso', 'disco', 'martillo', 'jabalina',
  'pentatlon', 'hexatlon', 'octatlon',
  '3000marcha', '5000marcha',
  '4x100m', '4x300m',
]);

export const EVENTS_SUB14: ReadonlySet<string> = new Set([
  '60m', '80m', '150m', '500m', '600m', '1000m', '2000m', '3000m',
  '60mv', '80mv', '220mv',
  'altura', 'pertiga', 'longitud', 'triple',
  'peso', 'disco', 'martillo', 'jabalina',
  'triatlon', 'pentatlon',
  '2000marcha', '3000marcha',
  '4x60m', '4x80m', '4x100m',
]);

export const EVENTS_SUB12: ReadonlySet<string> = new Set([
  '50m', '60m', '80m', '150m', '200m', '400m', '500m', '600m', '1000m', '2000m',
  '60mv', '80mv',
  'altura', 'pertiga', 'longitud',
  'peso', 'disco', 'jabalina',
  'triatlon',
  '1000marcha', '2000marcha',
  '4x50m', '4x60m', '4x80m',
]);

/** Programa individual del Cto. España de Clubes (División de Honor y
 *  Primera División). Sólo pruebas que puntúan en liga; quedan fuera
 *  relevos (puntúan al equipo), combinadas, 10000 m y marchas largas.
 *  La carrera larga del programa es 3000 m (no 5000 m). Las vallas
 *  cortas varían: 110 m vallas en hombres, 100 m vallas en mujeres. */
const EVENTS_DH_1D_BASE: ReadonlySet<string> = new Set([
  '100m', '200m', '400m', '800m', '1500m', '3000m',
  '400mv',
  '3000mobst',
  'altura', 'pertiga', 'longitud', 'triple',
  'peso', 'disco', 'martillo', 'jabalina',
  '5000marcha',
]);
export const EVENTS_DH_1D_H: ReadonlySet<string> = new Set([
  ...EVENTS_DH_1D_BASE,
  '110mv',
]);
export const EVENTS_DH_1D_M: ReadonlySet<string> = new Set([
  ...EVENTS_DH_1D_BASE,
  '100mv',
]);
/** Devuelve el set de pruebas del programa DH/1D para el género dado. */
export function eventsDh1dFor(genero: 'H' | 'M'): ReadonlySet<string> {
  return genero === 'H' ? EVENTS_DH_1D_H : EVENTS_DH_1D_M;
}
/** Unión H+M, útil para filtros que no conocen el género (p. ej. el
 *  atleta consultado del simulador de cambio de club: su SB se calcula
 *  primero sin filtrar y luego se compara contra el club; el género
 *  efectivo se infiere de las marcas). */
export const EVENTS_DH_1D: ReadonlySet<string> = new Set([
  ...EVENTS_DH_1D_H,
  ...EVENTS_DH_1D_M,
]);

export const EVENTS_KIDS: ReadonlySet<string> = new Set([
  '50m', '60m', '80m', '150m',
  'altura', 'longitud',
  'peso', 'jabalina',
  'triatlon',
  '4x50m', '4x60m',
]);

export function canonicalSetFor(categoria: string): ReadonlySet<string> {
  switch (categoria) {
    case 'Sub 8':
    case 'Sub 10':
      return EVENTS_KIDS;
    case 'Sub 12':
      return EVENTS_SUB12;
    case 'Sub 14':
      return EVENTS_SUB14;
    case 'Sub 16':
      return EVENTS_SUB16;
    case 'Sub 18':
      return EVENTS_SUB18;
    case 'Sub 20':
    case 'Sub 23':
    case 'Absoluta':
    case 'Master':
    default:
      return EVENTS_ADULT;
  }
}

/** Ordena por orden canónico (las pruebas conocidas primero, las
 *  desconocidas al final por orden alfabético). Recibe una función para
 *  extraer la pruebaKey de cada item. */
export function orderByCanonical<T>(items: T[], getKey: (it: T) => string): T[] {
  return [...items].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    const ia = CANONICAL_INDEX.get(ka) ?? 9999;
    const ib = CANONICAL_INDEX.get(kb) ?? 9999;
    if (ia !== ib) return ia - ib;
    return ka.localeCompare(kb, 'es');
  });
}
